<template>
    <jet-form-section-alternative @submitted="updateProfileInformation">
        <template #title>{{ $t("profile.profileInformation") }}</template>

        <template #description>
            {{ $t("profile.profileInformationInfo") }}
        </template>

        <template #form>
            <!-- Profile Photo -->
            <div
                class="col-span-6 sm:col-span-4"
                v-if="$page.props.jetstream.managesProfilePhotos"
            >
                <!-- Profile Photo File Input -->
                <input
                    type="file"
                    class="hidden"
                    ref="photo"
                    @change="updatePhotoPreview"
                />

                <jet-label for="photo" :value="$t('profile.photo')" />

                <!-- Current Profile Photo -->
                <div class="mt-2" v-show="!photoPreview">
                    <img
                        :src="user.profile_photo_url"
                        :alt="user.firstname"
                        class="rounded-full h-20 w-20 object-cover"
                    />
                </div>

                <!-- New Profile Photo Preview -->
                <div class="mt-2" v-show="photoPreview">
                    <span
                        class="
                            block
                            rounded-full
                            w-20
                            h-20
                            bg-cover bg-no-repeat bg-center
                        "
                        :style="'background-image: url(\'' + photoPreview + '\');'"
                    >
                    </span>
                </div>

                <jet-secondary-button
                    class="mt-2 mr-2"
                    type="button"
                    @click.prevent="selectNewPhoto"
                >
                    {{ $t('profile.selectNewPhoto') }}
                </jet-secondary-button>

                <jet-secondary-button
                    type="button"
                    class="mt-2"
                    @click.prevent="deletePhoto"
                    v-if="user.profile_photo_path"
                >
                    {{ $t('profile.removePhoto') }}
                </jet-secondary-button>

                <jet-input-error :message="form.errors.photo" class="mt-2" />
            </div>

            <div class="col-span-6 sm:col-span-4">
                <jet-label for="firstname" :value="$t('profile.firstname')" />
                <jet-input
                    id="firstname"
                    type="text"
                    class="mt-1 block w-full"
                    v-model="form.firstname"
                    autocomplete="firstname"
                />
                <jet-input-error :message="form.errors.firstname" class="mt-2" />
            </div>

            <div class="col-span-6 sm:col-span-4">
                <jet-label for="lastname" :value="$t('profile.lastname')" />
                <jet-input
                    id="lastname"
                    type="text"
                    class="mt-1 block w-full"
                    v-model="form.lastname"
                    autocomplete="lastname"
                />
                <jet-input-error :message="form.errors.lastname" class="mt-2" />
            </div>

            <!-- Nur Berater: die Nummer sehen Kaeufer als Ansprechperson. -->
            <div v-if="isAdvisor" class="col-span-6 sm:col-span-4">
                <jet-label for="mobile" :value="$t('profile.mobile')" />
                <jet-input
                    id="mobile"
                    type="tel"
                    class="mt-1 block w-full"
                    v-model="form.mobile"
                    autocomplete="tel"
                    placeholder="+41 79 123 45 67"
                />
                <jet-input-error :message="form.errors.mobile" class="mt-2" />
            </div>

            <!-- Email -->
            <div class="col-span-6 sm:col-span-4 mt-2">
                <jet-label for="email" :value="$t('profile.email')" />
                <jet-input
                    id="email"
                    type="email"
                    class="block w-full"
                    v-model="form.email"
                />
                <jet-input-error :message="form.errors.email" class="mt-2" />
            </div>
        </template>

        <template #actions>
            <primary-button
                class="mt-2"
                :class="{ 'opacity-25': form.processing }"
                :disabled="form.processing"
            >
                {{ $t("actions.save") }}
            </primary-button>
        </template>
    </jet-form-section-alternative>

    <!-- Crop Modal -->
    <jet-dialog-modal :show="showCropDialog" @close="closeCropDialog" max-width="4xl">
        <template #title>
            <div class="flex justify-between items-center w-full">
                <span>{{ $t('profile.cropPhoto') }}</span>
                <div class="flex items-center">
                    <span class="mr-3 text-xl font-bold text-gray-800">{{ $t('profile.crop') }}</span>
                    <label class="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" v-model="cropToggle" class="sr-only peer">
                        <div class="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-minplan-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-minplan-blue"></div>
                    </label>
                </div>
            </div>
        </template>
        <template #content>
            <div class="flex flex-col items-center">
                <p class="text-sm text-gray-500 mb-4">{{ $t('profile.cropInstructions') }}</p>
                <div class="relative bg-gray-100 rounded-lg shadow-inner overflow-auto border border-gray-200" style="max-height: 75vh;">
                    <div class="relative inline-block min-w-full">
                        <img :src="cropImageSrc" ref="cropImage" class="max-w-full h-auto block mx-auto" @load="initCropBox" />
                        <div
                            v-if="cropImageSrc && cropToggle"
                            class="absolute border-2 border-dashed border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.65)] z-10 cursor-move"
                            :style="{
                                left: cropBox.x + 'px',
                                top: cropBox.y + 'px',
                                width: displayCropWidth + 'px',
                                height: displayCropHeight + 'px',
                                outline: '1px solid rgba(0,0,0,0.3)'
                            }"
                            @mousedown="startDragging"
                        >
                            <!-- Resize Handles -->
                            <div class="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-minplan-blue rounded-full cursor-nwse-resize z-20" @mousedown.stop="startResizing('nw', $event)"></div>
                            <div class="absolute -top-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-minplan-blue rounded-full cursor-nesw-resize z-20" @mousedown.stop="startResizing('ne', $event)"></div>
                            <div class="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-minplan-blue rounded-full cursor-nesw-resize z-20" @mousedown.stop="startResizing('sw', $event)"></div>
                            <div class="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-minplan-blue rounded-full cursor-nwse-resize z-20" @mousedown.stop="startResizing('se', $event)"></div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <jet-secondary-button @click="closeCropDialog">{{ $t('actions.cancel') }}</jet-secondary-button>
            <primary-button @click="applyCrop" class="ml-2">{{ $t('actions.apply') }}</primary-button>
        </template>
    </jet-dialog-modal>
</template>

<script>
import { defineComponent } from "vue";
import PrimaryButton from "@/Jetstream/PrimaryButton.vue";
import JetInput from "@/Jetstream/Input.vue";
import JetInputError from "@/Jetstream/InputError.vue";
import JetLabel from "@/Jetstream/Label.vue";
import JetActionMessage from "@/Jetstream/ActionMessage.vue";
import JetSecondaryButton from "@/Jetstream/SecondaryButton.vue";
import JetFormSectionAlternative from "@/Jetstream/FormSectionAlternative.vue";
import JetDialogModal from "@/Jetstream/DialogModal.vue";

export default defineComponent({
    components: {
        JetActionMessage,
        PrimaryButton,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        JetFormSectionAlternative,
        JetDialogModal
    },

    props: {
        user: { type: Object, required: true },
        // Bewusst ohne Default: fehlt die Prop (Verdrahtung in Show.vue
        // gebrochen), sendet das Formular KEINE Nummer statt `null` — sonst
        // loeschte jedes Speichern des Namens still die gespeicherte Nummer.
        mobile: { type: String, required: false }
    },

    data() {
        return {
            form: this.$inertia.form({
                _method: "PUT",
                firstname: this.user.firstname,
                lastname: this.user.lastname,
                email: this.user.email,
                // Nur Berater senden die Nummer; der Server ignoriert sie bei
                // Kaeufern ohnehin.
                ...(this.user.type === "advisor" && this.mobile !== undefined
                    ? { mobile: this.mobile }
                    : {}),
                photo: null
            }),

            photoPreview: null,
            showCropDialog: false,
            cropImageSrc: null,
            originalFile: null,
            cropBox: { x: 0, y: 0 },
            displayCropWidth: 0,
            displayCropHeight: 0,
            cropToggle: true
        };
    },

    computed: {
        isAdvisor() {
            return this.user.type === "advisor";
        },
        isCropEnabled() {
            return this.$page.props.profile_photo_crop_mode === 'On';
        },
        cropWidth() {
            return this.$page.props.profile_photo_crop_width || 1000;
        },
        cropHeight() {
            return this.$page.props.profile_photo_crop_height || 1000;
        }
    },

    methods: {
        updateProfileInformation() {
            if (this.$refs.photo && !this.form.photo) {
                this.form.photo = this.$refs.photo.files[0];
            }

            this.form.post(this.route("user-profile-information.update"), {
                errorBag: "updateProfileInformation",
                preserveScroll: true,
                onSuccess: () => {
                    this.$notify({
                        group: "success",
                        title: "actions.success",
                        text: "profile.profileUpdated"
                    });
                    this.clearPhotoFileInput();
                    this.form.photo = null;
                }
            });
        },

        selectNewPhoto() {
            this.$refs.photo.click();
        },

        updatePhotoPreview() {
            const photo = this.$refs.photo.files[0];

            if (!photo) return;

            if (this.isCropEnabled) {
                this.originalFile = photo;
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.cropImageSrc = e.target.result;
                    this.cropToggle = true;
                    this.showCropDialog = true;
                };
                reader.readAsDataURL(photo);
            } else {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.photoPreview = e.target.result;
                };
                reader.readAsDataURL(photo);
            }
        },

        initCropBox() {
            const img = this.$refs.cropImage;
            if (!img) return;
            
            const ratio = img.naturalWidth / img.clientWidth;
            
            this.displayCropWidth = this.cropWidth / ratio;
            this.displayCropHeight = this.cropHeight / ratio;

            if (this.displayCropWidth > img.clientWidth || this.displayCropHeight > img.clientHeight) {
                const scale = Math.min(img.clientWidth / this.displayCropWidth, img.clientHeight / this.displayCropHeight);
                this.displayCropWidth *= scale * 0.9;
                this.displayCropHeight *= scale * 0.9;
            }

            this.cropBox.x = (img.clientWidth - this.displayCropWidth) / 2;
            this.cropBox.y = (img.clientHeight - this.displayCropHeight) / 2;
        },

        startDragging(e) {
            e.preventDefault();
            const startX = e.clientX;
            const startY = e.clientY;
            const initialX = this.cropBox.x;
            const initialY = this.cropBox.y;

            const move = (moveEvent) => {
                const dx = moveEvent.clientX - startX;
                const dy = moveEvent.clientY - startY;
                
                const container = this.$refs.cropImage;
                const maxX = container.clientWidth - this.displayCropWidth;
                const maxY = container.clientHeight - this.displayCropHeight;

                this.cropBox.x = Math.max(0, Math.min(maxX, initialX + dx));
                this.cropBox.y = Math.max(0, Math.min(maxY, initialY + dy));
            };

            const up = () => {
                window.removeEventListener('mousemove', move);
                window.removeEventListener('mouseup', up);
            };

            window.addEventListener('mousemove', move);
            window.addEventListener('mouseup', up);
        },

        startResizing(handle, e) {
            e.preventDefault();
            const startX = e.clientX;
            const startY = e.clientY;
            const initialX = this.cropBox.x;
            const initialY = this.cropBox.y;
            const initialWidth = this.displayCropWidth;
            const initialHeight = this.displayCropHeight;
            const aspectRatio = initialWidth / initialHeight;
            const container = this.$refs.cropImage;

            const move = (moveEvent) => {
                const dx = moveEvent.clientX - startX;
                const dy = moveEvent.clientY - startY;

                let newWidth, newHeight, newX, newY;

                if (handle === 'se') {
                    newWidth = Math.max(50, initialWidth + dx);
                    newHeight = newWidth / aspectRatio;
                    if (initialX + newWidth > container.clientWidth) {
                        newWidth = container.clientWidth - initialX;
                        newHeight = newWidth / aspectRatio;
                    }
                    if (initialY + newHeight > container.clientHeight) {
                        newHeight = container.clientHeight - initialY;
                        newWidth = newHeight * aspectRatio;
                    }
                    newX = initialX;
                    newY = initialY;
                } else if (handle === 'sw') {
                    const fixedRight = initialX + initialWidth;
                    newWidth = Math.max(50, initialWidth - dx);
                    newHeight = newWidth / aspectRatio;
                    if (fixedRight - newWidth < 0) {
                        newWidth = fixedRight;
                        newHeight = newWidth / aspectRatio;
                    }
                    if (initialY + newHeight > container.clientHeight) {
                        newHeight = container.clientHeight - initialY;
                        newWidth = newHeight * aspectRatio;
                    }
                    newX = fixedRight - newWidth;
                    newY = initialY;
                } else if (handle === 'ne') {
                    const fixedBottom = initialY + initialHeight;
                    newHeight = Math.max(50, initialHeight - dy);
                    newWidth = newHeight * aspectRatio;
                    if (initialX + newWidth > container.clientWidth) {
                        newWidth = container.clientWidth - initialX;
                        newHeight = newWidth / aspectRatio;
                    }
                    if (fixedBottom - newHeight < 0) {
                        newHeight = fixedBottom;
                        newWidth = newHeight * aspectRatio;
                    }
                    newX = initialX;
                    newY = fixedBottom - newHeight;
                } else if (handle === 'nw') {
                    const fixedRight = initialX + initialWidth;
                    const fixedBottom = initialY + initialHeight;
                    newHeight = Math.max(50, initialHeight - dy);
                    newWidth = newHeight * aspectRatio;
                    if (fixedRight - newWidth < 0) {
                        newWidth = fixedRight;
                        newHeight = newWidth / aspectRatio;
                    }
                    if (fixedBottom - newHeight < 0) {
                        newHeight = fixedBottom;
                        newWidth = newHeight * aspectRatio;
                    }
                    newX = fixedRight - newWidth;
                    newY = fixedBottom - newHeight;
                }

                this.displayCropWidth = newWidth;
                this.displayCropHeight = newHeight;
                this.cropBox.x = newX;
                this.cropBox.y = newY;
            };

            const up = () => {
                window.removeEventListener('mousemove', move);
                window.removeEventListener('mouseup', up);
            };

            window.addEventListener('mousemove', move);
            window.addEventListener('mouseup', up);
        },

        applyCrop() {
            if (!this.cropToggle) {
                this.photoPreview = this.cropImageSrc;
                this.form.photo = this.originalFile;
                this.showCropDialog = false;
                return;
            }

            const img = this.$refs.cropImage;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            const ratio = img.naturalWidth / img.clientWidth;

            const sourceWidth = this.displayCropWidth * ratio;
            const sourceHeight = this.displayCropHeight * ratio;

            canvas.width = sourceWidth;
            canvas.height = sourceHeight;

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            ctx.drawImage(
                img,
                this.cropBox.x * ratio,
                this.cropBox.y * ratio,
                sourceWidth,
                sourceHeight,
                0,
                0,
                sourceWidth,
                sourceHeight
            );

            canvas.toBlob((blob) => {
                const file = new File([blob], this.originalFile.name, { type: 'image/jpeg' });
                this.form.photo = file;
                this.photoPreview = canvas.toDataURL('image/jpeg');
                this.showCropDialog = false;
            }, 'image/jpeg', 0.95);
        },

        deletePhoto() {
            this.$inertia.delete(this.route("current-user-photo.destroy"), {
                preserveScroll: true,
                onSuccess: () => {
                    this.photoPreview = null;
                    this.clearPhotoFileInput();
                }
            });
        },

        clearPhotoFileInput() {
            if (this.$refs.photo?.value) {
                this.$refs.photo.value = null;
            }
        },

        closeCropDialog() {
            this.showCropDialog = false;
            this.clearPhotoFileInput();
        }
    }
});
</script>
